import { useDashboardWarningSettings } from "@/features/shared/hooks";
import { usePublicWarningPageDefaults } from "@/features/shared/hooks/use-warning-page-defaults";
import { riskPictureBadgeFill } from "@/features/shared/widgets/risk-picture/risk-picture-colors";
import {
  RISK_PICTURE_WARNING_LEVEL,
  type RiskPictureLevel,
} from "@/features/shared/widgets/risk-picture/risk-picture-model";
import { useRiskPictureLevel } from "@/features/shared/widgets/risk-picture/use-risk-picture-level";
import { useTranslation } from "@/lib/i18n";
import { readPublicWarningPageSettings } from "@/lib/public-warning-settings.utils";
import { warningLevelI18nKey } from "@/lib/warning-level";

export interface LandWarningIndicator {
  level: RiskPictureLevel;
  /** Solid issued-warning fill, same as the map badge. */
  color: string;
  /** Screen-reader text, e.g. "Highest warning: code orange". */
  label: string;
}

/**
 * Land warning page state for the toolbar button and the nav link.
 * `indicator` is only set while an issued warning (not a kansverwachting) is active.
 */
export function useLandWarning() {
  const { t } = useTranslation();
  const { selectedDashboard, publicWarningSettings } = useDashboardWarningSettings();
  const page = readPublicWarningPageSettings(
    publicWarningSettings,
    usePublicWarningPageDefaults(),
  );
  const enabled = Boolean(selectedDashboard) && page.enabled;
  const level = useRiskPictureLevel(enabled, "issued");

  const indicator: LandWarningIndicator | undefined = level && {
    level,
    color: riskPictureBadgeFill("issued", level).fill,
    label: String(
      t("riskPicture.navigationDot", {
        level: String(t(warningLevelI18nKey[RISK_PICTURE_WARNING_LEVEL[level]])),
      }),
    ),
  };

  return { selectedDashboard: enabled ? selectedDashboard : undefined, page, indicator };
}
